import { Link } from "@tanstack/react-router";
import {
	Car,
	ExternalLink,
	Flag,
	Home,
	Images,
	LogIn,
	Menu,
	Settings,
	Trophy,
	Users,
	Wrench,
	X,
	Zap,
} from "lucide-react";
import { useState } from "react";
import { authClient } from "@/lib/auth-client";
import ThemeToggle from "./ThemeToggle";

export default function Header() {
	const [isOpen, setIsOpen] = useState(false);
	const { data: session } = authClient.useSession();

	return (
		<>
			{/* Thin cherry racing stripe at the very top */}
			<div className="h-1 bg-cherry" />
			<header className="px-4 py-3 flex items-center justify-between bg-card text-foreground shadow-lg">
				<div className="flex items-center gap-3">
					<button
						onClick={() => setIsOpen(true)}
						className="p-2 hover:bg-secondary rounded-lg transition-colors"
						aria-label="Open menu"
					>
						<Menu size={24} />
					</button>
					<Link to="/" className="flex items-center gap-2">
						<Flag size={24} className="text-cherry" />
						<span className="text-2xl font-heading tracking-wider uppercase">
							Derby Manager
						</span>
					</Link>
				</div>
				<div className="flex items-center gap-2">
					<ThemeToggle />
					{session?.user ? (
						<div className="flex items-center gap-2 text-sm">
							<span className="hidden sm:inline text-muted-foreground">
								{session.user.name || session.user.email}
							</span>
							<button
								onClick={() => void authClient.signOut()}
								className="px-3 py-1.5 bg-secondary hover:bg-secondary/80 rounded text-sm transition-colors text-secondary-foreground"
							>
								Sign out
							</button>
						</div>
					) : (
						<Link
							to="/login"
							className="flex items-center gap-1.5 px-3 py-1.5 bg-primary hover:bg-accent text-primary-foreground font-semibold rounded text-sm transition-colors"
						>
							<LogIn size={16} />
							Sign in
						</Link>
					)}
				</div>
			</header>
			{/* Checkered flag divider below header */}
			<div className="checker-stripe h-1" />

			{isOpen && (
				<div
					className="fixed inset-0 bg-black/50 z-40"
					onClick={() => setIsOpen(false)}
				/>
			)}

			<aside
				className={`fixed top-0 left-0 h-full w-72 bg-background text-foreground shadow-2xl z-50 transform transition-transform duration-300 ease-in-out flex flex-col ${
					isOpen ? "translate-x-0" : "-translate-x-full"
				}`}
			>
				<div className="flex items-center justify-between p-4 border-b-2 border-cherry/40">
					<h2 className="text-xl">Menu</h2>
					<button
						onClick={() => setIsOpen(false)}
						className="p-2 hover:bg-secondary rounded-lg transition-colors"
						aria-label="Close menu"
					>
						<X size={24} />
					</button>
				</div>

				<nav className="flex-1 p-4 overflow-y-auto space-y-1">
					<Link
						to="/"
						onClick={() => setIsOpen(false)}
						className="flex items-center gap-3 p-3 rounded-lg hover:bg-secondary transition-colors"
						activeOptions={{ exact: true }}
						activeProps={{
							className:
								"flex items-center gap-3 p-3 rounded-lg bg-muted border-l-[3px] border-cherry transition-colors",
						}}
					>
						<Home size={20} />
						<span className="font-medium">Home</span>
					</Link>

					<Link
						to="/events"
						onClick={() => setIsOpen(false)}
						className="flex items-center gap-3 p-3 rounded-lg hover:bg-secondary transition-colors"
						activeProps={{
							className:
								"flex items-center gap-3 p-3 rounded-lg bg-muted border-l-[3px] border-cherry transition-colors",
						}}
					>
						<Trophy size={20} />
						<span className="font-medium">Events</span>
					</Link>

					{session?.user && (
						<>
							<Link
								to="/dashboard"
								onClick={() => setIsOpen(false)}
								className="flex items-center gap-3 p-3 rounded-lg hover:bg-secondary transition-colors"
								activeProps={{
									className:
										"flex items-center gap-3 p-3 rounded-lg bg-muted border-l-[3px] border-cherry transition-colors",
								}}
							>
								<Car size={20} />
								<span className="font-medium">My Races</span>
							</Link>

							<div className="border-t border-border my-3" />
							<p className="px-3 py-1 text-xs font-bold text-cherry/70 uppercase tracking-widest">
								Admin
							</p>
							<Link
								to="/admin/events"
								onClick={() => setIsOpen(false)}
								className="flex items-center gap-3 p-3 rounded-lg hover:bg-secondary transition-colors"
								activeProps={{
									className:
										"flex items-center gap-3 p-3 rounded-lg bg-muted border-l-[3px] border-cherry transition-colors",
								}}
							>
								<Settings size={20} />
								<span className="font-medium">Manage Events</span>
							</Link>
							<Link
								to="/admin/hardware-test"
								onClick={() => setIsOpen(false)}
								className="flex items-center gap-3 p-3 rounded-lg hover:bg-secondary transition-colors"
								activeProps={{
									className:
										"flex items-center gap-3 p-3 rounded-lg bg-muted border-l-[3px] border-cherry transition-colors",
								}}
							>
								<Wrench size={20} />
								<span className="font-medium">Hardware Test</span>
							</Link>
							<Link
								to="/admin/users"
								onClick={() => setIsOpen(false)}
								className="flex items-center gap-3 p-3 rounded-lg hover:bg-secondary transition-colors"
								activeProps={{
									className:
										"flex items-center gap-3 p-3 rounded-lg bg-muted border-l-[3px] border-cherry transition-colors",
								}}
							>
								<Users size={20} />
								<span className="font-medium">Users</span>
							</Link>
							<Link
								to="/admin/images"
								onClick={() => setIsOpen(false)}
								className="flex items-center gap-3 p-3 rounded-lg hover:bg-secondary transition-colors"
								activeProps={{
									className:
										"flex items-center gap-3 p-3 rounded-lg bg-muted border-l-[3px] border-cherry transition-colors",
								}}
							>
								<Images size={20} />
								<span className="font-medium">Images</span>
							</Link>
							<a
								href="https://flash.pinewood-derby.net"
								target="_blank"
								rel="noopener noreferrer"
								onClick={() => setIsOpen(false)}
								className="flex items-center gap-3 p-3 rounded-lg hover:bg-secondary transition-colors"
							>
								<Zap size={20} />
								<span className="font-medium">Firmware Flasher</span>
								<ExternalLink size={14} className="ml-auto text-muted-foreground" />
							</a>
						</>
					)}
				</nav>
			</aside>
		</>
	);
}
